/**
 * Aviso sonoro discreto ("pip") sintetizado na hora com a Web Audio API.
 *
 * Sintetizar em vez de carregar um arquivo evita um asset binário no bundle,
 * elimina a espera de download no primeiro toque e permite um envelope suave —
 * duas notas curtas em terça, sem o "click" típico de MP3 cortado.
 */

type AudioContextCtor = typeof AudioContext;

const MIN_INTERVAL_MS = 1200;

let audioContext: AudioContext | null = null;
let lastPlayedAt = 0;
let unlockBound = false;

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext || w.webkitAudioContext || null;
}

function getAudioContext(): AudioContext | null {
  const Ctor = getAudioContextCtor();
  if (!Ctor) return null;
  if (!audioContext) {
    try {
      audioContext = new Ctor();
    } catch {
      return null;
    }
  }
  return audioContext;
}

/**
 * Navegadores só liberam áudio depois de uma interação do usuário. Registrar o
 * "destravamento" no primeiro clique/tecla garante que o primeiro aviso da
 * sessão realmente toque.
 */
export function primeNotificationSound(): void {
  if (unlockBound || typeof window === 'undefined') return;
  unlockBound = true;

  const unlock = () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
  };

  window.addEventListener('pointerdown', unlock, { once: true, passive: true });
  window.addEventListener('keydown', unlock, { once: true });
}

/** Uma nota senoidal com ataque/decaimento suaves (sem estalo). */
function scheduleTone(
  ctx: AudioContext,
  frequency: number,
  startAt: number,
  duration: number,
  peakGain: number,
): void {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequency, startAt);

  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peakGain, startAt + 0.014);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

  oscillator.connect(gain);
  gain.connect(ctx.destination);

  oscillator.start(startAt);
  oscillator.stop(startAt + duration + 0.02);
}

/** Ganhos base das duas notas (multiplicados pelo volume do usuário em playTeamChatPip). */
const PIP_GAIN_FIRST = 0.14;
const PIP_GAIN_SECOND = 0.11;

/**
 * Toca o aviso de mensagem privada do chat interno.
 * `volume` é um multiplicador (1 = padrão); valores acima de 1 aumentam o volume.
 * Silencioso em caso de falha — som é acessório, nunca deve gerar erro na tela.
 */
export function playTeamChatPip(volume = 1): void {
  const now = Date.now();
  if (now - lastPlayedAt < MIN_INTERVAL_MS) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  const play = () => {
    try {
      const startAt = ctx.currentTime + 0.01;
      const gainScale = Math.max(0.25, Math.min(3, volume));
      // Lá5 → Dó#6: intervalo agradável e curto, típico de notificação sóbria.
      scheduleTone(ctx, 880, startAt, 0.11, PIP_GAIN_FIRST * gainScale);
      scheduleTone(ctx, 1108.73, startAt + 0.1, 0.16, PIP_GAIN_SECOND * gainScale);
      lastPlayedAt = now;
    } catch {
      /* sem som */
    }
  };

  if (ctx.state === 'suspended') {
    void ctx.resume().then(play).catch(() => undefined);
    return;
  }
  play();
}
